// "Ask about this" (presentations, design/2026-11-03-presentations.md): the
// dialog a presentation's Ask opens. Two ways on, in this order (Adam's
// decision): ask in the chat that made the presentation, or start a new chat
// that gets a copy of it. Neither sends anything: the first fills the
// composer, the second opens the start screen with the presentation as a chip.
import React from 'react'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined'
import DisabledReason from '../system/DisabledReason'

export interface PresentationAskDialogProps {
  open: boolean
  /** "Deck › Slide › Layer": what the person pressed Ask on. */
  breadcrumb: string
  /** The figure asked about, as the page shows it ("45% · Total × Q3"). */
  figureText?: string
  /** The slide's or layer's starting question; both ways on start from it. */
  question?: string
  /** Ask in the chat the presentation came from. Absent where there is no such chat (a dashboard). */
  onAskInChat?: () => void
  /** Why Ask in this chat is closed. */
  askInChatReason?: string
  onStartNewChat: () => void
  /** Why a new chat cannot start from here. */
  startNewChatReason?: string
  onClose: () => void
}

export default function PresentationAskDialog({
  open,
  breadcrumb,
  figureText,
  question,
  onAskInChat,
  askInChatReason,
  onStartNewChat,
  startNewChatReason,
  onClose,
}: PresentationAskDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth data-testid="presentation-ask-dialog">
      <DialogTitle sx={{ pb: 1 }}>Ask about this</DialogTitle>
      <DialogContent>
        {breadcrumb && (
          <Typography data-testid="presentation-ask-breadcrumb" sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }}>
            {breadcrumb}
          </Typography>
        )}
        {figureText && (
          <Typography data-testid="presentation-ask-figure" sx={{ fontSize: 13, color: 'text.secondary', mt: 0.5 }}>
            {figureText}
          </Typography>
        )}
        {question && (
          <Typography data-testid="presentation-ask-question" sx={{ fontSize: 14, fontStyle: 'italic', color: 'text.primary', mt: 1 }}>
            “{question}”
          </Typography>
        )}
        <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 1.5 }}>
          {onAskInChat
            ? 'Ask in this chat puts a note in the message box for you to finish. A new chat starts with a copy of this presentation.'
            : 'A new chat starts with a copy of this presentation, open at this point.'}
        </Typography>
        {/* The choices, stacked full width so neither wraps under the other; Cancel stays in the actions. */}
        <Stack direction="column" useFlexGap sx={{ gap: 1, mt: 2 }}>
          {onAskInChat && (
            <DisabledReason reason={askInChatReason}>
              <Button
                fullWidth
                variant="contained"
                color="info"
                startIcon={<ChatBubbleOutlineIcon />}
                onClick={onAskInChat}
                data-testid="presentation-ask-in-chat"
                sx={{ textTransform: 'none' }}
              >
                Ask in this chat
              </Button>
            </DisabledReason>
          )}
          <DisabledReason reason={startNewChatReason}>
            <Button
              fullWidth
              variant={onAskInChat ? 'outlined' : 'contained'}
              color="info"
              startIcon={<AddCommentOutlinedIcon />}
              onClick={onStartNewChat}
              data-testid="presentation-start-new-chat"
              sx={{ textTransform: 'none' }}
            >
              Start a new chat from here
            </Button>
          </DisabledReason>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
        <Button color="info" onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
      </DialogActions>
    </Dialog>
  )
}
